import create from 'zustand'
import { WritableDraft } from 'immer/dist/internal'
import { immer } from 'zustand/middleware/immer'
import {
  EducationLevelType,
  TeamEducationLevel,
  UserFieldsFragment,
} from '@/graphql/generated/graphql'
import {
  AuthUser,
  getAuthStateUser,
  useAuthUser,
} from '@/hooks/useAuth/zustand'
import { persist } from 'zustand/middleware'

type State = {
  selectedLevel: EducationLevelType
}

interface Actions {
  setState: (updater: (state: WritableDraft<State>) => void) => void
  reset: () => void
}

const defaultLevel = EducationLevelType.UnderGraduate

export const initialState: State = {
  selectedLevel: defaultLevel,
}

export const educationLevelTypeStore = create<State & Actions>()(
  persist(
    immer((set, get) => ({
      ...initialState,
      setState: (e) => {
        return set(e, undefined)
      },
      reset: () => {
        set(initialState, true)
      },
    })),
    {
      name: 'zSelectedEducationLevelType',
    },
  ),
)

export const selectedEducationLevelTypeFn = ({
  user,
  selectedLevel,
}: {
  user: Pick<UserFieldsFragment, 'Team'> | undefined
  selectedLevel: EducationLevelType
}) => {
  const levels: EducationLevelType[] =
    user?.Team.TeamEducationLevel.map(
      (el: TeamEducationLevel) => el.educationLevelType,
    ) || []

  if (levels.includes(selectedLevel)) {
    return selectedLevel
  }

  const firstLevel = levels[0]

  if (!firstLevel) return defaultLevel

  return firstLevel
}

export const getSelectedEducationLevelType = () => {
  const level = educationLevelTypeStore.getState().selectedLevel
  const user = getAuthStateUser()

  return selectedEducationLevelTypeFn({ user, selectedLevel: level })
}

export const useSelectedEducationLevelType = (): EducationLevelType => {
  const user = useAuthUser()
  const selectedLevel = educationLevelTypeStore((s) => s.selectedLevel)

  return selectedEducationLevelTypeFn({ user, selectedLevel })
}

export const useCertificateSelectedLevelLabel = () => {
  const selectedLevel = useSelectedEducationLevelType()

  if (selectedLevel === EducationLevelType.UnderGraduate) return 'Graduação'
  if (selectedLevel === EducationLevelType.PostGraduate) return 'Pós-graduação'

  return 'Certificados'
}

export const userHasLevelAndSelected = (
  user: AuthUser,
  level: EducationLevelType,
  selectedLevel: EducationLevelType,
) => {
  const levels: EducationLevelType[] =
    user?.Team.TeamEducationLevel.map(
      (el: TeamEducationLevel) => el.educationLevelType,
    ) || []
  return levels.includes(level) && selectedLevel === level
}

export const useVerifyViewPermission = (type: EducationLevelType) => {
  const selectedLevel = useSelectedEducationLevelType()

  return selectedLevel === type
}

export const useMyEducationLevels = (): EducationLevelType[] => {
  const user = useAuthUser()
  const educationLevelsPositions: Record<EducationLevelType, number> = {
    UNDER_GRADUATE: 0,
    POST_GRADUATE: 1,
    GENERIC_CERTIFICATES: 2,
  }

  const levels =
    user?.Team.TeamEducationLevel.map(
      (el: TeamEducationLevel) => el.educationLevelType,
    ).sort(
      (a, b) => educationLevelsPositions[a] - educationLevelsPositions[b],
    ) || []
  return levels
}

export const useHasBothPermissions = () => {
  const levels = useMyEducationLevels()

  return (
    levels.includes(EducationLevelType.UnderGraduate) &&
    levels.includes(EducationLevelType.PostGraduate)
  )
}
